import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Shield, ShieldOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function WhitelistManager() {
  const [entries, setEntries] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [currentIp, setCurrentIp] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ip: "", label: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/whitelist");
      setEntries(data.entries);
      setEnabled(data.enabled);
      setCurrentIp(data.current_ip);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleEnabled = async (val) => {
    if (val) {
      const adminAllowed = entries.find((e) => e.ip === currentIp);
      if (!adminAllowed) {
        const confirmIt = window.confirm(
          `Votre IP actuelle (${currentIp}) n'est pas dans la liste blanche. Si vous activez la restriction maintenant, vous risquez de perdre l'accès au site public. Activer quand même ?`
        );
        if (!confirmIt) return;
      }
    }
    try {
      await api.post("/admin/whitelist/toggle", { enabled: val });
      setEnabled(val);
      toast.success(val ? "Restriction IP activée" : "Restriction IP désactivée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/whitelist", form);
      setForm({ ip: "", label: "" });
      toast.success("IP ajoutée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const addCurrent = async () => {
    try {
      await api.post("/admin/whitelist", { ip: currentIp, label: "Mon IP actuelle" });
      toast.success("Votre IP a été ajoutée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Supprimer l'IP ${entry.ip} ?`)) return;
    try {
      await api.delete(`/admin/whitelist/${entry.id}`);
      toast.success("IP retirée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-whitelist-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// Sécurité</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Whitelist IP</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Restreindre l'accès au portail public à une liste d'adresses IP. Le backoffice reste toujours accessible.
        </p>
      </div>

      <section className="border border-border bg-card rounded-sm p-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {enabled ? (
            <Shield className="h-5 w-5 text-primary" />
          ) : (
            <ShieldOff className="h-5 w-5 text-muted-foreground" />
          )}
          <div>
            <p className="font-medium text-sm">
              Restriction par IP : {enabled ? "Activée" : "Désactivée"}
            </p>
            <p className="text-xs text-muted-foreground">
              Votre IP actuelle : <span className="font-mono text-foreground">{currentIp}</span>
            </p>
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={toggleEnabled} data-testid="whitelist-toggle" />
      </section>

      <section className="border border-border bg-card rounded-sm p-6">
        <h2 className="font-bold tracking-tight mb-4">Ajouter une IP</h2>
        <form onSubmit={add} className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs uppercase tracking-wider">Adresse IP</Label>
            <Input
              required
              placeholder="192.168.1.10"
              value={form.ip}
              onChange={(e) => setForm((f) => ({ ...f, ip: e.target.value }))}
              className="mt-2 font-mono"
              data-testid="whitelist-ip-input"
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs uppercase tracking-wider">Libellé (optionnel)</Label>
            <Input
              placeholder="Bureau Paris"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              className="mt-2"
              data-testid="whitelist-label-input"
            />
          </div>
          <button
            type="submit"
            className="h-10 px-4 inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
            data-testid="whitelist-add-button"
          >
            <Plus className="h-4 w-4" />
            Ajouter
          </button>
          <button
            type="button"
            onClick={addCurrent}
            className="h-10 px-4 inline-flex items-center gap-2 rounded-sm border border-border text-sm font-medium hover:bg-accent"
            data-testid="whitelist-add-current-button"
          >
            Ajouter mon IP
          </button>
        </form>
      </section>

      <section className="border border-border bg-card rounded-sm">
        <header className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-bold tracking-tight">IPs autorisées</h2>
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {entries.length} entrée{entries.length > 1 ? "s" : ""}
          </span>
        </header>
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground">Chargement…</p>
        ) : entries.length === 0 ? (
          <p className="p-12 text-center text-sm text-muted-foreground" data-testid="whitelist-empty">
            Aucune IP autorisée pour l'instant.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {entries.map((e) => (
              <li
                key={e.id}
                className="flex items-center gap-4 p-4"
                data-testid={`whitelist-row-${e.id}`}
              >
                <Shield className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono font-medium">{e.ip}</p>
                  {e.label && <p className="text-xs text-muted-foreground">{e.label}</p>}
                </div>
                <button
                  onClick={() => remove(e)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
                  data-testid={`whitelist-delete-${e.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
